import c3dgl from 'maplibre-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import ReactDOM from 'react-dom';
import LayerControls from './LayerControls';
import { stylesMap } from './mapboxMapStyles';
import { transformHelper } from './helper';

const mapInterface = {
  displayedMap: null,
  geocoderCtrl: null,
  navigationCtrl: null,
  geolocateCtrl: null,
  layerCtrl: null,

  renderMap: function(
    {
      container,
      mapControls,
      analytics,
      mapStyle,
      mapRendering,
      tourId,
      mapToken,
    },
    onError
  ) {
    c3dgl.accessToken = mapToken;
    this.displayedMap = new c3dgl.Map({
      container: container,
      style: this.buildMapStyle(mapStyle),
      center: [-98.55562, 39.809734], // USA Center
      zoom: 4,
    });
    this.displayedMap.on('error', ({ error }) => {
      console.log('c3dgl error', error.message);
      !window.Cypress && onError(error);
    });
    this.displayedMap.addControl(new c3dgl.AttributionControl(), 'bottom-left');
    this.displayedMap._controlPositions['bottom-left']?.setAttribute(
      'data-cy',
      'map-attributions'
    );
    mapControls && this.addMapControls(mapControls, analytics);
    mapRendering && this.reAddRasterLayer(tourId, mapRendering);
  },

  addMapControls: function({ nav, layers, geolocate, geocoder }, analytics) {
    // expose geocoder for use in stopeditor -> dropping markers on search result
    this.geocoderCtrl = new MapboxGeocoder({
      accessToken: c3dgl.accessToken,
      placeholder: 'Search for a location',
    });
    this.navigationCtrl = new c3dgl.NavigationControl();
    this.layerCtrl = new LayerControls(analytics);
    this.geolocateCtrl = new c3dgl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: true,
    });
    nav.show && this.setNavToolTips(this.navigationCtrl, nav.pos);
    layers.show && this.displayedMap.addControl(this.layerCtrl, layers.pos);
    geolocate.show && this.setGeoToolTip(this.geolocateCtrl, geolocate.pos);
    geocoder.show &&
      this.displayedMap.addControl(this.geocoderCtrl, geocoder.pos);

    // geocoder search prioritization by current map location
    this.displayedMap.on('load', () => this.updateGeocoderProximity());
    this.displayedMap.on('moveend', () => this.updateGeocoderProximity());
  },

  removeMapControls: function() {
    this.displayedMap.removeControl(this.layerCtrl);
    this.displayedMap.removeControl(this.geocoderCtrl);
    this.displayedMap.removeControl(this.navigationCtrl);
    this.displayedMap.removeControl(this.geolocateCtrl);
  },

  updateLayerControls: function({ publicTour, currentTour }) {
    if (this.layerCtrl) {
      this.layerCtrl.currentTour = currentTour;
      this.layerCtrl.publicTour = publicTour;
    }
  },

  updateGeocoderProximity: function() {
    // if zoomed in enough for geocoder proximity to matter. IE. not viewing the entire globe
    if (this.displayedMap.getZoom() > 8.5) {
      const center = this.displayedMap.getCenter().wrap();
      this.geocoderCtrl.setProximity({
        longitude: center.lng,
        latitude: center.lat,
      });
    } else {
      this.geocoderCtrl.setProximity(null);
    }
  },

  setNavToolTips: function(nav, position) {
    const navBtns = [
      {
        selector: '.mapboxgl-ctrl-zoom-in',
        name: 'Zoom In',
        className: 'zoom-in',
      },
      {
        selector: '.mapboxgl-ctrl-zoom-out',
        name: 'Zoom Out',
        className: 'zoom-out',
      },
      {
        selector: '.mapboxgl-ctrl-compass',
        name: 'Rotate View',
        className: 'rotate',
      },
    ];
    this.displayedMap.addControl(nav, position);
    navBtns.forEach(({ selector, name, className }) => {
      const btn = document.querySelector(selector);
      const tooltip = document.createElement('span');
      tooltip.className = `tooltip ${className}`;
      tooltip.innerHTML = name;

      btn?.addEventListener('mouseenter', () => {
        btn.appendChild(tooltip);
      });
      btn?.addEventListener('mouseleave', () => {
        btn.removeChild(tooltip);
      });
    });

    this.setAriaZoomLabels();
    this.displayedMap.on('zoomend', this.setAriaZoomLabels.bind(this));
  },

  setAriaZoomLabels: function() {
    const zoomInBtn = document.querySelector('.mapboxgl-ctrl-zoom-in');
    const zoomOutBtn = document.querySelector('.mapboxgl-ctrl-zoom-out');

    if (zoomInBtn && zoomOutBtn) {
      const zoom = Math.floor(this.displayedMap.getZoom());
      zoomInBtn.ariaLabel = `Zoom in, current zoom level ${zoom}`;
      zoomOutBtn.ariaLabel = `Zoom out, current zoom level ${zoom}`;
    }
  },

  setGeoToolTip: function(geoCtrl, position) {
    this.displayedMap.addControl(geoCtrl, position);
    const container = geoCtrl._container;
    const tooltip = document.createElement('span');
    tooltip.className = 'tooltip geolocate';
    tooltip.innerHTML = 'Your Location';

    container?.addEventListener('mouseenter', () => {
      container.appendChild(tooltip);
    });
    container?.addEventListener('mouseleave', () => {
      container.removeChild(tooltip);
    });
  },

  getMap: function() {
    return this.displayedMap;
  },

  getContainer: function() {
    return this.displayedMap.getContainer();
  },

  getCenter: function() {
    return this.displayedMap.getCenter();
  },

  setNewMapBounds: function(coords) {
    const bounds = coords.reduce((bounds, coord) => {
      return bounds.extend(coord);
    }, new c3dgl.LngLatBounds(coords[0], coords[0]));
    return bounds;
  },

  createMarker: function(markerData, callback) {
    const {
      markerCoords,
      className,
      order,
      panoId,
      stopName,
      changeStop,
      draggable,
      tourName,
      highlighted,
    } = markerData;

    const markerEl = document.createElement('div');
    markerEl.className = className;
    markerEl.setAttribute(
      'aria-label',
      `Tour stop #${order + 1} : ${stopName}, marker`
    );
    markerEl.setAttribute('role', 'button');
    markerEl.setAttribute('tabindex', '0');
    markerEl.style.backgroundImage = panoId
      ? `url('https://s3.us-east-2.amazonaws.com/c3dtourbuilderx/media/numbered_tour_icons/number_${order +
          1}.png')`
      : `url('https://s3.us-east-2.amazonaws.com/c3dtourbuilderx/media/icon.png')`;
    const marker = new c3dgl.Marker(markerEl, {
      offset: [0, -17.5],
    })
      .setLngLat(markerCoords)
      .addTo(this.displayedMap);

    let [markerHeight, markerRadius, linearOffset] = [20, 10, 10];
    const popupOffsets = {
      top: [0, 0],
      'top-left': [0, 0],
      'top-right': [0, 0],
      bottom: [0, markerHeight + 30],
      'bottom-left': [
        linearOffset,
        (markerHeight - markerRadius + linearOffset) * -1,
      ],
      'bottom-right': [
        -linearOffset,
        (markerHeight - markerRadius + linearOffset) * -1,
      ],
      left: [markerRadius + 10, markerHeight + markerRadius],
      right: [-markerRadius - 10, markerHeight + markerRadius],
    };

    const tourNamePopup = new c3dgl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: popupOffsets,
    });

    const markerElMouseEnter = () => {
      markerEl.classList.add('enlarge');

      tourName &&
        tourNamePopup
          .setLngLat(markerCoords)
          .setHTML(`<p>${tourName}</p>`)
          .addTo(this.displayedMap);

      tourName && tourNamePopup._content.classList.add('tour-tooltip');
      tourName && tourNamePopup._container.classList.add('tour-tooltip');
    };

    const removePopup = () => {
      if (!highlighted) markerEl.classList.remove('enlarge');
      tourNamePopup.remove();
    };

    const markerKeypressListener = e => {
      const code = e.charCode || e.keyCode;
      if (code === 32 || code === 13) {
        this.displayedMap.panTo(markerCoords, { zoom: 15 });
        typeof changeStop === 'function' &&
          changeStop({
            stopNum: order,
            stopName,
            origin: 'Map Marker - Change Stop',
          });
      }
    };

    const markerClickListener = () => {
      tourNamePopup.remove();
      !draggable && this.displayedMap.panTo(markerCoords);
      typeof changeStop === 'function' &&
        changeStop({
          stopNum: order,
          stopName,
          origin: 'Map Marker - Change Stop',
        });
      typeof callback === 'function' && callback();
    };

    markerEl.addEventListener('mouseenter', markerElMouseEnter);
    markerEl.addEventListener('mouseleave', removePopup);
    markerEl.addEventListener('focus', () => {
      markerElMouseEnter();
      !draggable && this.displayedMap.panTo(markerCoords);
    });
    markerEl.addEventListener('blur', removePopup);
    markerEl.addEventListener('keypress', markerKeypressListener);
    markerEl.addEventListener('click', markerClickListener);

    return marker;
  },

  deleteMarker: function(marker) {
    marker.remove();
  },

  createMarkerWithPopup: function(markerData, component, callback) {
    const {
      markerCoords,
      className,
      order,
      draggable,
      stopName,
      changeStop,
      highlighted,
    } = markerData;
    const markerEl = document.createElement('div');
    markerEl.className = className;
    markerEl.setAttribute(
      'aria-label',
      `Tour stop #${order + 1} : ${stopName}, marker`
    );
    markerEl.setAttribute('role', 'button');
    markerEl.style.backgroundImage = `url('https://s3.us-east-2.amazonaws.com/c3dtourbuilderx/media/numbered_tour_icons/number_${order +
      1}.png')`;
    markerEl.setAttribute('data-order', order + 1);
    markerEl.setAttribute('data-cy', `stop-num-${order + 1}`);
    const marker = new c3dgl.Marker(markerEl, {
      offset: [0, -15],
    })
      .setLngLat(markerCoords)
      .addTo(this.displayedMap);

    const markerClickListener = () => {
      this.displayedMap.panTo(markerCoords, { zoom: 15 });
      typeof changeStop === 'function' &&
        changeStop({
          stopNum: order,
          stopName,
          origin: 'Map Marker - Change Stop',
        });
    };

    const markerKeypressListener = e => {
      const code = e.charCode || e.keyCode;
      if (code === 32 || code === 13) {
        this.displayedMap.panTo(markerCoords, { zoom: 15 });
        typeof changeStop === 'function' &&
          changeStop({
            stopNum: order,
            stopName,
            origin: 'Map Marker - Change Stop',
          });
      }
    };

    const popupMouseLeaveListener = e => {
      const relTargetClass =
        e.relatedTarget && e.relatedTarget.getAttribute('class'); // e.relatedTarget && checks for null if mouse leaves popup onto users browser etc
      if (
        relTargetClass === null ||
        !relTargetClass.includes('mapboxgl-marker')
      ) {
        marker.getPopup().remove();
      }
    };

    const markerMouseEnterListener = () => {
      markerEl.classList.add('enlarge');
      if (!marker.getPopup().isOpen()) {
        marker.getPopup().addTo(this.displayedMap);
        ReactDOM.render(component, marker.getPopup()._content);

        marker
          .getPopup()
          ._container.addEventListener('mouseleave', popupMouseLeaveListener);

        typeof callback === 'function' &&
          callback({
            stopNum: order,
            stopName,
            origin: 'Open Popup',
          });
      }
    };

    const markerMouseLeaveListener = e => {
      if (marker.getPopup().isOpen()) {
        const relTargetClass =
          e.relatedTarget && e.relatedTarget.getAttribute('class'); // e.relatedTarget && checks for null if mouse leaves marker onto users browser etc
        if (relTargetClass === null || !relTargetClass.includes('popup')) {
          marker.getPopup().remove();
        }
      }
    };

    const markerBlurListener = e => {
      if (marker.getPopup().isOpen()) {
        const relTargetClass =
          e.relatedTarget && e.relatedTarget.getAttribute('class'); // e.relatedTarget && checks for null if keyboard leaves marker focus
        if (relTargetClass === null || !relTargetClass.includes('popup')) {
          if (!highlighted) markerEl.classList.remove('enlarge');
          marker.getPopup().remove();
        }
      }
    };

    const markerFocusListener = e => {
      markerEl.classList.add('enlarge');
      !draggable && this.displayedMap.panTo(markerCoords);
      if (!marker.getPopup().isOpen()) {
        marker.getPopup().addTo(this.displayedMap);
        ReactDOM.render(component, marker.getPopup()._content);

        marker
          .getPopup()
          ._container.addEventListener('blur', markerBlurListener);

        typeof callback === 'function' &&
          callback({
            stopNum: order,
            stopName,
            origin: 'Open Popup',
          });
      }
    };

    if (!draggable) {
      marker.setPopup(
        new c3dgl.Popup({ offset: 25, closeButton: false }).setHTML('')
      );

      if (!marker.getPopup()._listeners) {
        marker.getPopup().on('close', () => {
          ReactDOM.unmountComponentAtNode(marker.getPopup()._content);
          if (!highlighted) markerEl.classList.remove('enlarge');
        });
      }

      markerEl.addEventListener('click', markerClickListener);

      markerEl.addEventListener('keypress', markerKeypressListener);

      markerEl.addEventListener('mouseenter', markerMouseEnterListener);

      markerEl.addEventListener('mouseleave', markerMouseLeaveListener);

      markerEl.addEventListener('focus', markerFocusListener);

      markerEl.addEventListener('blur', markerBlurListener);
    }
    return marker;
  },

  createRadarMarker: function(markerData) {
    const { lng, lat } = markerData;
    const markerEl = document.createElement('div');
    markerEl.className = 'tour-stop-marker-fixed c3dgl-marker enlarge';
    markerEl.style.backgroundImage =
      'url("https://s3.us-east-2.amazonaws.com/c3dtourbuilderx/media/radar.png")';

    const marker = new c3dgl.Marker(markerEl)
      .setLngLat([lng, lat])
      .addTo(this.displayedMap);
    return marker;
  },

  rotateRadarMarker: function(marker, radarPos, northPos) {
    const style = marker._element.style;
    const newTransform = transformHelper(style.transform, radarPos, northPos);
    style.transform = newTransform;

    this.displayedMap.on('move', () => {
      style.transform = transformHelper(style.transform, radarPos, northPos);
    });

    this.displayedMap.on('moveend', () => {
      style.transform = transformHelper(style.transform, radarPos, northPos);
    });
    return marker;
  },

  updateMarkerNumber: function(marker, order) {
    const style = marker._element.style;
    style.backgroundImage = `url("https://s3.us-east-2.amazonaws.com/c3dtourbuilderx/media/numbered_tour_icons/number_${order +
      1}.png")`;
    return marker;
  },

  updateMarkerImage: function(marker, radarPos, northPos) {
    marker._element.classList.add('enlarge');
    const style = marker._element.style;
    const radarPng =
      'url("https://s3.us-east-2.amazonaws.com/c3dtourbuilderx/media/radar.png")';
    const backgroundUrl =
      style.backgroundImage !== radarPng ? radarPng : style.backgroundImage;
    style.backgroundImage = backgroundUrl;
    style.transform = transformHelper(style.transform, radarPos, northPos);
    return marker;
  },

  makeMarkerDraggable: function(marker, dragStartCallback, dragEndCallback) {
    marker.setDraggable(true);
    if (dragStartCallback) {
      marker.on('dragstart', dragStartCallback);
    }
    if (dragEndCallback) {
      marker.on('dragend', dragEndCallback);
    }
  },

  setMarkerLocation: function(marker, lat, lng) {
    const LngLat = new c3dgl.LngLat(lng, lat);
    marker.setLngLat(LngLat);
  },

  disableMarkerDragging: function(marker) {
    marker.setDraggable(false);
  },

  getMapStyle: function() {
    if (this.displayedMap?.isStyleLoaded()) {
      return this.displayedMap.getStyle().name;
    }
    // this will return undefined in updateMapStyle but still allows stylesMap[currentMapStyle] !== newMapStyle to be met
    // this is not ideal but covers an edge case when previewing a tour/stop and using the browser back button to navigate back
    return;
  },

  updateMapStyle: function(newMapStyle, tourId = '', renderingUrl = '') {
    const currentMapStyle = this.getMapStyle(); // in some case after using browser back this will return undefined

    // checks if incoming newMapStyle is already in use as current map style
    if (this.displayedMap && stylesMap[currentMapStyle] !== newMapStyle) {
      const newStyle = this.buildMapStyle(newMapStyle);
      this.displayedMap.setStyle(newStyle);
      // if a rendering layer exists then we re add the layer
      return (
        tourId && renderingUrl && this.reAddRasterLayer(tourId, renderingUrl)
      );
    }

    // if the users default Tour map is streets & they're accessing the Tour Editor from CMS '/home' which has a
    // default map style of streets already
    if (newMapStyle === 'streets' && stylesMap[currentMapStyle] === 'streets') {
      // if a rendering layer exists then we add the layer
      return (
        tourId && renderingUrl && this.addRasterLayer(tourId, renderingUrl)
      );
    }
  },

  buildMapStyle: function(newMapStyle) {
    if (newMapStyle === 'streets') {
      return `${process.env.REACT_APP_C3D_TILE_SOURCE}${process.env.REACT_APP_C3D_STYLE}/style.json`;
    }
    return `mapbox://styles/mapbox/${
      newMapStyle === 'scenic'
        ? 'cj8gg22et19ot2rnz65958fkn'
        : `${newMapStyle}-v9`
    }`;
  },

  reAddRasterLayer: function(id, layerUrl) {
    // if re-addings an existing layer we need to wait for the update of map style to finish to prevent errors
    this.displayedMap.once('styledata', () => {
      this.addRasterLayer(id, layerUrl);
    });
  },

  addRasterLayer: function(id, layerUrl, componentCB) {
    if (typeof componentCB === 'function') {
      // callback fires after layer has been added to map
      componentCB();
    }
    if (window.Cypress) return false;

    const source = this.displayedMap.getSource(id);
    const layer = this.displayedMap.getLayer(id);
    if (!source && !layer) {
      this.displayedMap.addLayer({
        id,
        type: 'raster',
        source: {
          type: 'raster',
          tileSize: 256,
          scheme: 'tms',
          tiles: [layerUrl],
        },
        'source-layer': id,
      });
    }
  },

  removeLayer: function(id) {
    const source = this.displayedMap.getSource(id);
    const layer = this.displayedMap.getLayer(id);
    if (source && layer) {
      this.displayedMap.removeLayer(id);
      this.displayedMap.removeSource(id);
    }
  },

  drawMapPolygon: function(coordsArray) {
    const id = 'route';
    const source = this.displayedMap.getSource(id);
    if (source) {
      source.setData({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: coordsArray,
        },
      });
    } else {
      this.displayedMap.addLayer({
        id: id,
        type: 'line',
        source: {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: coordsArray,
            },
          },
        },
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#888',
          'line-width': 6,
        },
      });
    }
  },

  panTo: function(lat, lng, zoom) {
    if (!lat || !lng) return;
    zoom = zoom || this.displayedMap.getZoom();
    this.displayedMap.panTo(new c3dgl.LngLat(lng, lat), { zoom });
  },
};

export default mapInterface;
